import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart, Calendar, Download } from 'lucide-react';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { masterSheetService } from '../forms/sites/Axon/services/axonApiService';
import { MasterSheetDataRow } from '../forms/sites/Axon/services/axonApiService';
import { ApiResponse } from '../forms/shared/services/baseApiService';
import DashboardSidebar from './DashboardSidebar';

const HistogramChartPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [chartData, setChartData] = useState<Array<{ date: string; energyExport: number }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30); // Default to last 30 days
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  });

  useEffect(() => {
    loadChartData();
  }, [dateRange, site]);

  const loadChartData = async () => {
    if (!site) return;

    setLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem('access_token') ||
                    localStorage.getItem('access_token') ||
                    sessionStorage.getItem('auth_token') ||
                    localStorage.getItem('auth_token') ||
                    localStorage.getItem('token') || '';
      const siteCode = (site || 'axon001').toLowerCase();
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api').replace(/\/api$/, '/api/v1');
      const apiUrl = `${apiBase}/master-sheet/range/?start_date=${dateRange.start}&end_date=${dateRange.end}&site_code=${encodeURIComponent(siteCode)}`;

      const apiResponse = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Site-Code': siteCode,
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      let response: ApiResponse<MasterSheetDataRow[]>;
      if (apiResponse.ok) {
        const data = await apiResponse.json();
response = {
          ok: true,
          status: apiResponse.status,
          data: Array.isArray(data) ? data : (data.results || data.data || []),
          error: null
        };
      } else {
        const errorText = await apiResponse.text();
        let errorData: { message?: string; error?: string };
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText || apiResponse.statusText };
        }
        response = {
          ok: false,
          status: apiResponse.status,
          data: null,
          error: errorData.message || errorData.error || 'Failed to fetch data'
        };
      }

      if (response.ok && response.data) {
        // Log for debugging
if (Array.isArray(response.data)) {


          // Transform data for chart - backend already filters by site
          const transformed: Array<{ date: string; energyExport: number }> = response.data
            .map((row: any, index: number) => {
              // Get export_mwh (Energy Export) - try multiple field names
              let energyExport = 0;

              // Check for export_mwh field (primary)
              if (row.export_mwh !== undefined && row.export_mwh !== null && row.export_mwh !== '') {
                const val = Number(row.export_mwh);
                if (!isNaN(val)) {
                  energyExport = val;
                }
              }

              // Log first row for debugging
              if (index === 0) {
}

              // Only include rows with valid dates
              if (!row.entry_date) {
return null;
              }

              return {
                date: row.entry_date,
                energyExport: energyExport
              };
            })
            .filter((item): item is { date: string; energyExport: number } => {
              return item !== null && item !== undefined && !!item.date;
            })
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
if (transformed.length > 0) {
            setChartData(transformed);
            setError(null); // Clear any previous errors
          } else if (Array.isArray(response.data) && response.data.length === 0) {
            // Case: API returned empty list which is valid outcome
            setChartData([]);
            setError(null); // No error, just no data
          } else {
            // Case: API returned data but filter removed everything (e.g. invalid dates)
            setError(`Data received but no valid entries found. Check console for details.`);
            setChartData([]);
          }
        } else {
          setError(`Invalid response format. Expected array, got: ${typeof response.data}`);
          setChartData([]);
        }
      } else {
        setError(response.error || 'Failed to load data');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/dashboard/${site}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);

    return `${day}${month}${year}`;
  };

  return (
    <div className="dashboard-chart-page" style={{
      height: '100vh',
      // Clear the fixed 64px app header so the page title is not hidden under it
      paddingTop: 64,
      boxSizing: 'border-box',
      display: 'flex',
      fontFamily: '"Inter", "Segoe UI", "Trebuchet MS", sans-serif',
      backgroundColor: '#F3F4F6'
    }}>
      {/* Sidebar */}
      {site && (
        <DashboardSidebar
          site={site}
          activeCategory="performance-analytics"
          onSelectCategory={() => navigate(`/dashboard/${site}`)}
        />
      )}

      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '64px',
          padding: '0 24px',
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E5E7EB',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
        }}>
          {/* Left Side: Back & Title */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <button
              onClick={handleBack}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#4B5563',
                backgroundColor: '#F9FAFB',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F3F4F6';
                e.currentTarget.style.color = '#1F2937';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#F9FAFB';
                e.currentTarget.style.color = '#4B5563';
              }}
            >
              <ArrowLeft size={16} />
              <span>Back</span>
            </button>

            <div style={{
              width: '1px',
              height: '24px',
              backgroundColor: '#E5E7EB'
            }} />

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                backgroundImage: 'linear-gradient(135deg, #0084CF 0%, #006FAE 100%)',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0, 132, 207, 0.2)'
              }}>
                <BarChart size={20} color="#FFFFFF" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#111827',
                  lineHeight: '1.2'
                }}>
                  Histogram Analysis
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#6B7280',
                  lineHeight: '1.2',
                  marginTop: '2px',
                  fontWeight: '500'
                }}>
                  {site?.toUpperCase()}
                </div>
              </div>
            </div>
          </div>


        </div>

        {/* Chart Container */}
        <div style={{
          flex: 1,
          padding: '24px',
          overflow: 'auto'
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#0084CF',
                  borderRadius: '12px',
                  marginBottom: '16px'
                }}>
                  <BarChart size={32} color="#FFFFFF" />
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#111827',
                  marginBottom: '8px'
                }}>
                  Loading Chart Data
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#6B7280'
                }}>
                  {site?.toUpperCase()}
                </div>
              </div>
            </div>
          ) : error ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}>
              <div style={{
                textAlign: 'center',
                padding: '24px',
                backgroundColor: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: '8px',
                maxWidth: '500px'
              }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#DC2626',
                  marginBottom: '8px'
                }}>
                  Error Loading Data
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#991B1B'
                }}>
                  {error}
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                marginBottom: '20px',
                borderBottom: '1px solid #E5E7EB',
                paddingBottom: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'end'
              }}>
                <div>
                  <h2 className="histogram-page-title" style={{
                    fontSize: '20px',
                    marginBottom: '4px'
                  }}>
                    Energy Export by Date
                  </h2>
                  <p className="histogram-page-subtitle" style={{
                    fontSize: '14px'
                  }}>
                    Histogram showing energy export (MWh) for each date
                  </p>
                </div>

                {/* Filters */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  {/* Range Preset Dropdown */}
                  <div style={{ position: 'relative' }}>
                    <select
                      className="histogram-control-bold"
                      onChange={(e) => {
                        const preset = e.target.value;
                        const end = new Date();
                        const start = new Date();

                        switch (preset) {
                          case '7d': start.setDate(end.getDate() - 7); break;
                          case '1m': start.setMonth(end.getMonth() - 1); break;
                          case '3m': start.setMonth(end.getMonth() - 3); break;
                          case '6m': start.setMonth(end.getMonth() - 6); break;
                          case '1y': start.setFullYear(end.getFullYear() - 1); break;
                          case 'all': setDateRange({ start: '', end: '' }); return;
                          default: return;
                        }

                        setDateRange({
                          start: start.toISOString().split('T')[0],
                          end: end.toISOString().split('T')[0]
                        });
                      }}
                      defaultValue="1m"
                      style={{
                        appearance: 'none',
                        padding: '8px 32px 8px 12px',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '6px',
                        border: '1px solid #E5E7EB',
                        fontSize: '13px',
                        cursor: 'pointer',
                        outline: 'none',
                        fontFamily: 'inherit',
                        minWidth: '130px',
                        backgroundImage: `url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="%236B7280" viewBox="0 0 16 16"><path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/></svg>')`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 8px center',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#D1D5DB'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; }}
                    >
                      <option value="7d">Last 7 Days</option>
                      <option value="1m">Last Month</option>
                      <option value="3m">Last 3 Months</option>
                      <option value="6m">Last 6 Months</option>
                      <option value="1y">Last Year</option>
                      <option value="all">All Time</option>
                    </select>
                  </div>

                  {/* Date Inputs */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '6px',
                    border: '1px solid #E5E7EB',
                    padding: '0 4px',
                    overflow: 'hidden',
                    height: '36px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 8px',
                      borderRight: '1px solid #F3F4F6',
                      position: 'relative',
                      minWidth: '110px'
                    }}>
                      <Calendar size={14} color="#6B7280" style={{ marginRight: '6px' }} />
                      <span className="histogram-control-bold">
                        {formatDisplayDate(dateRange.start)}
                      </span>
                      <input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                        style={{
                          opacity: 0,
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                    <div className="histogram-control-bold" style={{
                      padding: '0 8px',
                      fontSize: '12px',
                    }}>
                      to
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 8px',
                      position: 'relative',
                      minWidth: '100px'
                    }}>
                      <span className="histogram-control-bold">
                        {formatDisplayDate(dateRange.end)}
                      </span>
                      <input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        style={{
                          opacity: 0,
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ flex: 1, minHeight: '400px', position: 'relative' }}>
                {chartData.length === 0 && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    pointerEvents: 'none'
                  }}>
                    <div className="histogram-no-data-text" style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      fontSize: '14px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      No data available for selected date range
                    </div>
                  </div>
                )}
                <div className="histogram-chart-container" style={{ width: '100%', height: '100%', minHeight: '400px' }}>
                  <ResponsiveContainer width="100%" height="100%" minHeight={400}>
                    <RechartsBarChart
                      data={chartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatDate}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        stroke="#6B7280"
                        tick={{ fontSize: 12 }}
                        interval={Math.max(0, Math.floor(chartData.length / 15))} // Show fewer labels if many dates
                      />
                      <YAxis
                        label={{ value: 'Energy Export (MWh)', angle: -90, position: 'insideLeft', style: { fontSize: '12px', fill: '#374151' } }}
                        stroke="#6B7280"
                        tick={{ fontSize: 12 }}
                        domain={['auto', 'auto']}
                        allowDecimals={true}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          fontSize: '13px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                        }}
                        formatter={(value: any) => {
                          const numValue = Number(value);
                          return [`${isNaN(numValue) ? '0.000' : numValue.toFixed(3)} MWh`, 'Energy Export'];
                        }}
                        labelFormatter={(label) => `Date: ${formatDate(label)}`}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: '13px', color: '#374151' }}
                      />
                      <Bar
                        dataKey="energyExport"
                        fill="#0084CF"
                        name="Energy Export (MWh)"
                        radius={[4, 4, 0, 0]}
                        isAnimationActive={true}
                        fontWeight={500}
                      />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistogramChartPage;



